// Who the pharmacist is handing the medicines to, at the Deliver step.
//
// The dispensing service requires handover_to to be a UUID and records it as
// the receipt attribution, so the field was a free-text box the pharmacist
// typed a raw uuid4 into by hand. Everything needed to fill it is already at
// hand: the linked IPD prescription carries nurse_id/nurse_name (stamped at
// order time from the bed's active assignment) and bed_no.
//
// The bed's CURRENT assignment wins over the prescription's stamped one when
// both exist — a dose ordered on the morning round is often collected on a
// later shift, so who is on the bed now is the better guess at who is at the
// counter. The stamped nurse is the fallback, because nurse_id is only
// populated on roughly half of prescriptions (it lands empty when the bed had
// no active assignment at order time) while bed_no is always present.
//
// Nothing here decides identity for accountability purposes — this is the
// pharmacist asserting who they handed the meds to, exactly as before, minus
// the typing. Self-asserted receipt is a separate step (the Confirm endpoint,
// which derives the nurse from their own JWT).

// nurse_id is a uuid4 by design; the dispensing service 422s on anything else.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isRecipientId = (value) => UUID_RE.test(String(value ?? "").trim());

const text = (value) => String(value ?? "").trim();

// "Priya Nair · Morning" — a name a pharmacist can sanity-check, which is the
// whole point of not showing them a uuid.
function optionFrom(nurseId, nurseName, note) {
  const value = text(nurseId);
  if (!value) return null;
  const name = text(nurseName);
  return { value, label: name || value, note: text(note), hasName: Boolean(name) };
}

// Most recently assigned first. assigned_at is an ISO string, which sorts
// lexicographically, so a plain compare is enough.
const byAssignedAtDesc = (a, b) => text(b.assigned_at).localeCompare(text(a.assigned_at));

/**
 * @param rx           the linked IPD prescription (may be null)
 * @param assignments  active NurseBedAssignment rows for rx.bed_no (may be [])
 * @returns { value, label, options, source }
 *          value   — what to submit as handover_to ("" when nothing is known)
 *          options — every nurse worth offering, deduped, best first
 *          source  — "bed" | "prescription" | "" (drives the hint shown)
 */
export function resolveHandoverRecipient(rx, assignments) {
  const live = (Array.isArray(assignments) ? assignments : [])
    .slice()
    .sort(byAssignedAtDesc)
    .map((a) => optionFrom(a?.nurse_id, a?.nurse_name, a?.shift))
    .filter(Boolean);

  const stamped = optionFrom(rx?.nurse_id, rx?.nurse_name, "on the order");

  const options = [];
  const seen = new Set();
  [...live, ...(stamped ? [stamped] : [])].forEach((opt) => {
    if (seen.has(opt.value)) return;
    seen.add(opt.value);
    options.push(opt);
  });

  const chosen = live[0] || stamped || null;
  return {
    value: chosen ? chosen.value : "",
    label: chosen ? chosen.label : "",
    options,
    source: live[0] ? "bed" : (stamped ? "prescription" : ""),
  };
}
